import { Alerta, Button, Card, DataList, Dialog, MontoField, PercentField, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { AporteGrupo, Grupo, MiembroGrupo } from "@/lib/api";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import { etiquetaPorcentaje } from "@/lib/grupos";
import { useGruposTab } from "./useGruposTab";
import { VistaPreviaGrupo } from "./VistaPreviaGrupo";

const columnasMiembros = [
  {
    clave: "nombre",
    titulo: "Fiduciante",
    render: (m: MiembroGrupo) => m.nombre,
  },
  {
    clave: "pct",
    titulo: "%",
    render: (m: MiembroGrupo) => etiquetaPorcentaje(m.porcentaje),
  },
  {
    clave: "grupo",
    titulo: "Con el grupo",
    render: (m: MiembroGrupo) => formatMoneda(m.con_el_grupo_usd, "USD"),
  },
  {
    clave: "suyo",
    titulo: "Por su cuenta",
    render: (m: MiembroGrupo) => formatMoneda(m.por_su_cuenta_usd, "USD"),
  },
  {
    clave: "saldo",
    titulo: "Saldo",
    render: (m: MiembroGrupo) => formatMoneda(m.saldo_usd, "USD"),
  },
];

const columnasAportes = [
  {
    clave: "fecha",
    titulo: "Fecha",
    render: (a: AporteGrupo) => formatFecha(a.fecha),
  },
  {
    clave: "monto",
    titulo: "Monto",
    render: (a: AporteGrupo) => formatMoneda(a.monto, a.moneda),
  },
  {
    clave: "tc",
    titulo: "Tipo de cambio",
    render: (a: AporteGrupo) => (a.tipo_cambio ? formatNumero(a.tipo_cambio) : "—"),
  },
  {
    clave: "usd",
    titulo: "En USD",
    render: (a: AporteGrupo) => formatMoneda(a.monto_usd, "USD"),
  },
  {
    clave: "reparto",
    titulo: "Reparto",
    render: (a: AporteGrupo) => a.partes.map((p) => `${p.nombre} ${formatMoneda(p.monto, a.moneda)}`).join(" · "),
  },
  { clave: "nota", titulo: "Nota", render: (a: AporteGrupo) => a.nota ?? "—" },
];

export function GruposTab() {
  const t = useGruposTab();
  const d = t.dialogo;
  const listo = !t.cargando && !t.errorCarga;

  const accionesAporte = (a: AporteGrupo) => a.bloqueo ? (
    <span className="text-chico text-texto-suave">{a.bloqueo}</span>
  ) : (
    <Row espacio={2}>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar aporte del ${formatFecha(a.fecha)} de ${a.grupo}`} onClick={() => t.abrirAporte(a)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar aporte del ${formatFecha(a.fecha)} de ${a.grupo}`} onClick={() => t.abrirBorrarAporte(a)}>
        Borrar
      </Button>
    </Row>
  );

  const tarjeta = (g: Grupo) => (
    <Card key={g.id}>
      <Stack espacio={3}>
        <Row justificar="entre" envolver espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">{g.nombre}</h2>
          {t.esAdmin && (
            <Row espacio={2}>
              <Button variante="secundario" tamanio="sm" aria-label={`Editar ${g.nombre}`} onClick={() => t.abrirGrupo(g)}>
                Editar
              </Button>
              {!g.tiene_aportes && (
                <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${g.nombre}`} onClick={() => t.abrirBorrarGrupo(g)}>
                  Borrar
                </Button>
              )}
            </Row>
          )}
        </Row>
        <DataList etiqueta={`Miembros de ${g.nombre}`} columnas={columnasMiembros} filas={g.miembros} claveFila={(m) => m.fiduciante_id} />
        <p className="text-chico text-texto-suave">
          Con el grupo: {formatMoneda(g.totales.con_el_grupo_usd, "USD")} · Por su cuenta: {formatMoneda(g.totales.por_su_cuenta_usd, "USD")} · Saldo:{" "}
          {formatMoneda(g.totales.saldo_usd, "USD")}
        </p>
        <h3 className="font-semibold">Aportes del grupo</h3>
        <DataList
          etiqueta={`Aportes de ${g.nombre}`}
          columnas={columnasAportes}
          filas={t.aportesDe(g)}
          claveFila={(a) => a.id}
          acciones={t.esAdmin ? accionesAporte : undefined}
          vacio="Todavía no aportó. Se carga en Aportes → Registrar aporte → Un grupo."
        />
      </Stack>
    </Card>
  );

  const titulo = d?.tipo === "grupo" && d.grupo ? "Editar grupo" : "Nuevo grupo";
  const fa = t.formAporte;

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Grupos</h1>
        {t.esAdmin && <Button onClick={() => t.abrirGrupo(null)}>Nuevo grupo</Button>}
      </Row>
      <p className="text-texto-suave">
        Fiduciantes que aportan juntos (por ejemplo, desde su empresa), cada uno con su % fijo. Un aporte del grupo se reparte entre ellos por esos % y queda en la cuenta
        de cada uno.
      </p>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {listo && t.grupos.length === 0 && <p className="text-texto-suave">Todavía no hay grupos.</p>}
      {listo && t.grupos.map(tarjeta)}

      <Dialog abierto={d?.tipo === "grupo"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={titulo}>
        <form onSubmit={(e) => void t.guardarGrupo(e)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Nombre" ayuda="Ej.: Empresa X" value={t.form.nombre} onChange={(e) => t.cambiarNombre(e.target.value)} error={t.erroresGrupo.nombre} />
            {t.miembrosFijos ? (
              <p className="text-chico text-texto-suave">El grupo ya aportó: sus fiduciantes y sus % no se cambian. Sí podés cambiarle el nombre.</p>
            ) : (
              <Stack espacio={3}>
                {t.form.lineas.map((l, i) => (
                  <Row key={i} envolver espacio={2} alinear="fin">
                    <Select
                      etiqueta={`Fiduciante ${i + 1}`}
                      opciones={t.opcionesMiembro(i)}
                      valor={l.fiduciante_id}
                      onCambio={(v) => t.cambiarLinea(i, "fiduciante_id", v)}
                      className="min-w-48"
                    />
                    <PercentField etiqueta={`% del fiduciante ${i + 1}`} valor={l.porcentaje} onCambio={(v) => t.cambiarLinea(i, "porcentaje", v)} className="w-28" />
                    {t.form.lineas.length > 2 && (
                      <Button variante="fantasma" tamanio="sm" aria-label={`Quitar fila ${i + 1}`} onClick={() => t.quitarLinea(i)}>
                        Quitar
                      </Button>
                    )}
                  </Row>
                ))}
                <div>
                  <Button variante="secundario" tamanio="sm" onClick={t.agregarLinea}>
                    Agregar fiduciante
                  </Button>
                </div>
                <p className="text-chico" aria-live="polite">
                  {t.textoSuma}
                </p>
                <Alerta texto={t.erroresGrupo.lineas} />
              </Stack>
            )}
            <Alerta texto={t.errorGuardarGrupo} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardandoGrupo}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog
        abierto={d?.tipo === "aporte"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo={d?.tipo === "aporte" ? `Editar aporte de ${d.grupo.nombre}` : "Editar aporte"}
      >
        {fa && (
          <form onSubmit={(e) => void t.guardarAporte(e)} noValidate>
            <Stack espacio={4}>
              <TextField etiqueta="Fecha" type="date" value={fa.fecha} onChange={(e) => t.cambiarAporte("fecha", e.target.value)} error={t.erroresAporte.fecha} />
              {t.opcionesCaja && (
                <Select etiqueta="Caja" opciones={t.opcionesCaja} valor={fa.caja_id} onCambio={(v) => t.cambiarAporte("caja_id", v)} placeholder="Elegí la caja" />
              )}
              <MontoField
                etiqueta="Monto"
                valor={fa.monto}
                onCambio={(v) => t.cambiarAporte("monto", v)}
                moneda={fa.moneda}
                onCambioMoneda={(m) => t.cambiarAporte("moneda", m)}
                error={t.erroresAporte.monto}
              />
              {fa.moneda === "ARS" && (
                <TextField
                  etiqueta="Tipo de cambio (ARS por USD)"
                  inputMode="decimal"
                  value={fa.tipo_cambio}
                  onChange={(e) => t.cambiarAporte("tipo_cambio", e.target.value)}
                  error={t.erroresAporte.tipo_cambio}
                />
              )}
              <TextField etiqueta="Nota" ayuda="Opcional" value={fa.nota} onChange={(e) => t.cambiarAporte("nota", e.target.value)} error={t.erroresAporte.nota} />
              <VistaPreviaGrupo partes={t.vistaPrevia} moneda={fa.moneda} />
              <Alerta texto={t.errorGuardarAporte} />
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoAporte}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog
        abierto={d?.tipo === "borrarGrupo" || d?.tipo === "borrarAporte"}
        onCambio={(abierto) => !abierto && t.cerrar()}
        titulo={d?.tipo === "borrarGrupo" ? "Borrar grupo" : "Borrar aporte del grupo"}
      >
        <Stack espacio={4}>
          {d?.tipo === "borrarGrupo" && <p>¿Borrar el grupo {d.grupo.nombre}? Sus fiduciantes quedan libres para otro grupo.</p>}
          {d?.tipo === "borrarAporte" && (
            <p>
              ¿Borrar el aporte de {d.aporte.grupo} del {formatFecha(d.aporte.fecha)} por {formatMoneda(d.aporte.monto, d.aporte.moneda)}? Se borra la parte de cada uno (
              {d.aporte.partes.length}).
            </p>
          )}
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
